import { Location } from '@angular/common';
import { computed, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class BackNavService {
    private readonly router = inject(Router);
    private readonly location = inject(Location);

    private readonly navigationCount = signal(0);
    private readonly previousUrl = signal<string | null>(null);
    private currentUrl = this.router.url;

    previousRoute = computed<string | null>(() => {
        if (this.navigationCount() <= 1) {
            return '/';
        }
        return this.previousUrl();
    });

    constructor() {
        this.router.events
            .pipe(
                filter((e): e is NavigationEnd => e instanceof NavigationEnd),
                takeUntilDestroyed()
            )
            .subscribe((event) => {
                this.previousUrl.set(this.currentUrl);
                this.currentUrl = event.urlAfterRedirects;
                this.navigationCount.update((c) => c + 1);
            });
    }

    /**
     * Navigates back using the browser's history stack (`history.back()`), not
     * a forward navigation to the previous URL. This preserves scroll position,
     * form state, and other browser-managed context that a `router.navigateByUrl()`
     * call would discard.
     *
     * When there is no in-app history (e.g., the user deep-linked directly),
     * falls back to a forward navigation to the application root.
     */
    back(): void {
        if (this.navigationCount() > 1) {
            this.location.back();
        } else {
            this.router.navigateByUrl('/');
        }
    }
}
